import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import './NotificationCenter.css';

function NotificationCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Where a notification should take you when tapped (by related_type + role)
  const targetFor = (n) => {
    const isParent = user?.role === 'parent';
    switch (n.related_type) {
      case 'message':
      case 'announcement': return '/communication';
      case 'waiver': return isParent ? '/sign-waivers' : '/waivers';
      case 'invoice': return isParent ? '/payment-portal' : '/invoices';
      case 'video': return '/video';
      case 'goal':
      case 'goal_reminder': return (isParent || user?.role === 'coach' || user?.role === 'admin') ? '/goals/review' : '/goals';
      default: return null;
    }
  };
  const handleClick = (n) => {
    if (!n.is_read) markRead(n.id);
    const to = targetFor(n);
    setOpen(false);
    if (to) navigate(to);
  };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/notifications/unread-count`);
      setUnreadCount(data.count || 0);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/api/notifications?limit=15`);
      setNotifications(data.notifications || []);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API_BASE_URL}/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/notifications/${id}`);
      const wasUnread = notifications.find(n => n.id === id && !n.is_read);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const typeIcon = {
    invoice_created: '💳',
    payment_received: '✅',
    payment_failed: '❌',
    waiver_reminder: '📋',
    waiver_signed: '✍️',
    injury_alert: '🚨',
    class_cancellation: '🚫',
    schedule_change: '📅',
    coach_approved: '🎉',
    announcement: '📢',
    skill_milestone: '⭐',
    meet_reminder: '🏆',
    general: '🔔'
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="notification-center" ref={dropdownRef}>
      <button
        className="notification-bell"
        onClick={() => setOpen(o => !o)}
        aria-label={`Notifications — ${unreadCount} unread`}
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <span className="dropdown-title">Notifications</span>
            {unreadCount > 0 && (
              <button className="mark-all-btn" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notif-empty">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notif-empty">No notifications yet.</div>
            ) : (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  className={`notif-item ${!notif.is_read ? 'unread' : ''} ${targetFor(notif) ? 'clickable' : ''}`}
                  onClick={() => handleClick(notif)}
                >
                  <span className="notif-icon">{typeIcon[notif.type] || '🔔'}</span>
                  <div className="notif-content">
                    <div className="notif-title">{notif.title}</div>
                    <div className="notif-message">{notif.message}</div>
                    <div className="notif-time">{timeAgo(notif.created_at)}</div>
                  </div>
                  <button
                    className="notif-delete"
                    onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                    aria-label="Delete notification"
                  >×</button>
                </div>
              ))
            )}
          </div>

        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
